import tw, { TwStyle } from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { Info } from '@icons/Info';
import { ExportIcon } from '@icons/ExportIcon';
import { Button } from '@components/Button';
import { ToolTip } from '@insights_module/components/ToolTip';
import { SidebarItem } from '@insights_module/components/Sidebar';
import { ContextualTierBadge } from '@insights_module/components/ContextualTierBadge';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { useEpisodeContextual } from '@insights_module/hooks/useEpisodeContextual';
import { download } from '@utils/download';
import { useInsightsContext } from '@insights_module/context/InsightsContext';

const Separator = tw.div`w-full h-[1px] bg-gray-1 mb-[4px]`;
const Title = tw.span`text-left text-[14px] font-semibold text-text-and-icon-muted tracking-[0.14px] px-[8px]`;
const Row = tw.div`grid grid-cols-[minmax(40px,auto)_1fr_auto] gap-[12px] justify-start rounded-[4px] mb-[4px] p-[6px]`;
const Cell = tw.div`text-[12px] font-semibold tracking-[0.12px] text-text-and-icon-muted-2 text-left flex items-center justify-start`;
const ContextualCategory = tw.div`text-text-and-icon font-medium truncate text-[13px]`;
const Fallback = tw.div`bg-gray-2 py-4 px-3 rounded-md text-text-and-icon-muted text-[13px] box-border mt-2 ml-[-4px] mr-1`;
const StyledButton = tw(
  Button.Primary
)`w-full flex items-center justify-start py-[10px] px-[12px] h-auto [&_svg]:fill-current [&_svg]:w-4 [&_svg]:h-4 [&_svg]:transition-all [&_svg]:duration-200`;
const selectedRowStyle = tw`bg-gray-2 border border-solid border-gray-1` as TwStyle;

export const EpisodeContextualSection = () => {
  const { t } = useTranslation();
  const { contextualData, isLoading } = useEpisodeContextual();
  const { selectedContextualCategory, setSelectedContextualCategory } = useEpisodeDetailsContext();

  const handleSelectCategory = (category: string) => {
    if (selectedContextualCategory === category) {
      setSelectedContextualCategory(null);
      return;
    }

    setSelectedContextualCategory(category);
  };

  return (
    <SidebarItem hasDivider={true} isLoading={isLoading} header={null}>
      <Title>
        {t('insights.episode_details_drawer.episode_details_sidebar.iab_categories.table.title')}
      </Title>
      <div>
        <Row tw='p-[8px] mb-0'>
          <Cell>
            {t(
              'insights.episode_details_drawer.episode_details_sidebar.iab_categories.table.head_1'
            )}
          </Cell>
          <Cell>
            {t(
              'insights.episode_details_drawer.episode_details_sidebar.iab_categories.table.head_2'
            )}
          </Cell>
          <Cell tw='flex items-center justify-end gap-[4px]'>
            {t(
              'insights.episode_details_drawer.episode_details_sidebar.iab_categories.table.head_3'
            )}
            {/* TODO: Get content for tooltip */}
            {/* <ToolTip content={'TODO: Get content for tooltip'} placement='right'>
              <Info tw='stroke-text-and-icon-muted-2 w-[16px] h-[16px]' />
            </ToolTip> */}
          </Cell>
        </Row>
        <Separator />
        {!isLoading && !contextualData.length ? (
          <Fallback>
            {t(
              'insights.episode_details_drawer.episode_details_sidebar.iab_categories.table.no_data'
            )}
          </Fallback>
        ) : (
          contextualData.map((category) => {
            return (
              <Row
                key={category.id}
                onClick={() => handleSelectCategory(category.name)}
                tw='cursor-pointer'
                css={[
                  tw`bg-page-background`,
                  category.name === selectedContextualCategory && selectedRowStyle,
                ]}>
                <Cell>
                  <ContextualTierBadge tier={category.taxonomy[0].tier} />
                </Cell>
                <Cell>
                  <ContextualCategory
                    css={[
                      category.name === selectedContextualCategory &&
                        tw`text-text-and-icon font-bold`,
                    ]}>
                    {category.display_name}
                  </ContextualCategory>
                </Cell>
                <Cell>
                  <div>{Math.round(category.score * 100)}%</div>
                </Cell>
              </Row>
            );
          })
        )}
      </div>
    </SidebarItem>
  );
};

export const DownloadContextualButton = () => {
  const { t } = useTranslation();
  const { contextualData } = useEpisodeContextual();
  const { selectedEpisode } = useInsightsContext();

  const handleDownloadClick = () => {
    const file = new Blob(
      [
        contextualData
          .map((item) => {
            return `${item.display_name} - ${item.score * 100}%`;
          })
          .join('\n'),
      ],
      {
        type: 'text/plain',
      }
    );

    const fileName = `contextual-data-${selectedEpisode}.txt`;

    download(file, fileName);
  };

  return (
    <StyledButton onClick={handleDownloadClick}>
      <ExportIcon />
      {t('insights.episode_details_drawer.episode_details_sidebar.iab_categories.download_button')}
    </StyledButton>
  );
};
